import { useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { useApiQuery } from '../useApiQuery';
import { useApiMutation } from '../useApiMutation';
import { contactKeys } from '@/lib/queryKeys';
import { contactEndpoints, contactStatusEndpoints } from '@/lib/api/endpoints';
import { apiGet, apiPost, operationsClient } from '@/lib/api';
import type { Contact, ContactStatus } from '@/types/contact';
import { normalizeContact } from './useContacts';

export function useContactStatuses(options?: { enabled?: boolean }) {
  return useApiQuery<ContactStatus[]>({
    queryKey: contactKeys.statuses(),
    queryFn: () => apiGet<ContactStatus[]>(operationsClient, contactStatusEndpoints.list()),
    enabled: options?.enabled ?? true,
    showErrorToast: false,
  });
}

export function useSetContactStatus() {
  const queryClient = useQueryClient();

  return useApiMutation<Contact, { contactId: string; status: ContactStatus }>({
    mutationFn: async ({ contactId, status }) => {
      const raw = await apiPost<Record<string, unknown>, { status: string; color: string }>(
        operationsClient,
        contactEndpoints.status(contactId),
        {
          status: status.name,
          color: status.color,
        },
      );
      return normalizeContact(raw);
    },
    onMutate: async ({ contactId, status }) => {
      await queryClient.cancelQueries({ queryKey: contactKeys.detail(contactId) });
      const previous = queryClient.getQueryData<Contact>(contactKeys.detail(contactId));
      if (previous) {
        queryClient.setQueryData<Contact>(contactKeys.detail(contactId), {
          ...previous,
          customStatus: status.name,
          customStatusColor: status.color,
        });
      }
      return { previous };
    },
    onError: (_error, variables, context) => {
      const previous = (context as { previous?: Contact } | undefined)?.previous;
      if (previous) {
        queryClient.setQueryData(contactKeys.detail(variables.contactId), previous);
      }
    },
    onSuccess: (contact) => {
      void queryClient.invalidateQueries({ queryKey: contactKeys.lists() });
      void queryClient.invalidateQueries({ queryKey: contactKeys.detail(contact.id) });
      toast.success('Contact status updated');
    },
  });
}
